import { computed, inject, Injectable, Injector, Signal } from '@angular/core';
import { User } from '@core/models/user.model';
import { NavigationService } from '@core/navigation/services/navigation.service';
import { AppPathsMap } from '@core/routing/app-paths';
import { GlobalLoadingService } from '@core/services/global-loading.service';
import { RecentOrganizationsService } from '@core/services/recent-organizations.service';
import { signalToObservable } from '@core/utils/signal.utilities';
import { OrganizationService } from '@features/organization/services/organization.service';
import { ProcessDocumentFacade } from '@features/risk-analysis/facades/process-document.facade';
import { RiskAnalysisService } from '@features/risk-analysis/services/risk-analysis.service';
import { RiskAnalysisStore } from '@features/risk-analysis/store/risk-analysis.store';
import { WorkItemTypeService } from '@features/work-item/kyc/services/work-item-type.service';
import type { UUID } from '@kerberos-compliance/compliance-shared-ts/common/types/uuid.type';
import { ResponseUserGrantDto } from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/dtos/response.user-grant.dto';
import {
  NamedRole,
  NamedRoleMap,
} from '@kerberos-compliance/compliance-shared-ts/core/iam/authorization/enums/named-role';
import { ResponseLeanOrganizationDto } from '@kerberos-compliance/compliance-shared-ts/features/organization/dtos/response.lean-organization.dto';
import {
  clearExcept,
  createAsyncErrorHandler,
  createErrorMetadata,
  ErrorLoggingService,
  ErrorSeverityMap,
  LoggerService,
  NotificationService,
  StorageService,
  TranslationService,
} from '@kerberos-compliance/compliance-shared-ui';
import { firstValueFrom } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { AuthService } from '@/auth/services/auth.service';
import { InactivityService } from '@/auth/services/inactivity.service';
import { AuthStore } from '@/auth/store/auth.store';
import {
  AuthSource,
  AuthSourceMap,
  AuthStateTokenAndOwnGrant,
  AuthStateTokens,
  FeaturesAmlDeskWithGrants,
  GrantLoadingStateMap,
  LogoutReason,
} from '@/auth/types/auth.types';
import { resolveAuthSource } from '@/auth/utils/auth-source.utilities';
import { ContractStore } from '@/contract/store/contract.store';
import { StorageServiceKeysMap } from '@/storage/types/storage.types';

type SetSessionGrantOptions = {
  suppressNotification?: boolean;
};

@Injectable({ providedIn: 'root' })
export class AuthFacade {
  private readonly authStore = inject(AuthStore);

  private readonly authService = inject(AuthService);

  private readonly notificationService = inject(NotificationService);

  private readonly translationService = inject(TranslationService);

  private readonly workItemTypeService = inject(WorkItemTypeService);

  private readonly contractStore = inject(ContractStore);

  private readonly inactivityService = inject(InactivityService);

  private readonly storageService = inject(StorageService);

  private readonly recentOrganizationsService = inject(RecentOrganizationsService);

  private readonly injector = inject(Injector);

  private readonly logger = inject(LoggerService);

  public readonly asyncErrorHandler = createAsyncErrorHandler();

  private readonly globalLoadingService = inject(GlobalLoadingService);

  private readonly errorLoggingService = inject(ErrorLoggingService);

  private readonly navigationService = inject(NavigationService);

  private readonly organizationService = inject(OrganizationService);

  private readonly riskAnalysisService = inject(RiskAnalysisService);

  private readonly riskAnalysisStore = inject(RiskAnalysisStore);

  private readonly processDocumentFacade = inject(ProcessDocumentFacade);

  private isLoggingOut = false;

  // Pass-through signals from the store
  public readonly user = this.authStore.user;

  public readonly logoutReason = this.authStore.logoutReason;

  public readonly ownGrants = this.authStore.ownGrants;

  public readonly organizations = this.authStore.organizations;

  public readonly organizationId = this.authStore.organizationId;

  public readonly organizationIndustry = this.authStore.organizationIndustry;

  public readonly grantLoadingComplete = this.authStore.grantLoadingComplete;

  public readonly grantLoadingState = this.authStore.grantLoadingState;

  public readonly authTokens = this.authStore.authTokens;

  public readonly authSourceMarker = this.authStore.authSourceMarker;

  public readonly authSource = computed<AuthSource>(() =>
    resolveAuthSource(this.authStore.authTokens().accessTokenKerberos, this.authStore.authSourceMarker()),
  );

  public readonly isHankoSession = computed<boolean>(() => this.authSource() === AuthSourceMap.Hanko);

  /**
   * Whether the session is fully established — the ADP platform token is present,
   * so requests can be authenticated. This is the strict definition: use it when the
   * answer has to reflect a session the app can actually act on, such as deciding
   * where an unmatched URL should land.
   */
  public readonly hasEstablishedSession = computed<boolean>(() =>
    Boolean(this.authStore.authTokens().accessTokenKerberos),
  );

  /**
   * Whether a session is established *or* still being established. The provider
   * marker is persisted the moment the Hanko login starts its token exchange, before
   * the platform token exists, so this is the definition route activation needs — it
   * keeps an in-flight login from being locked out of the route it is about to reach.
   *
   * Deliberately more permissive than `hasEstablishedSession`: the marker also
   * survives a *failed* exchange (see `HankoAuthService`), so a true answer here does
   * not guarantee a usable session.
   */
  public readonly hasSessionInFlightOrEstablished = computed<boolean>(
    () => this.authStore.authSourceMarker() !== undefined || this.hasEstablishedSession(),
  );

  public readonly isUserLoggedIn = this.authStore.isUserLoggedIn;

  public readonly roleDisplayNames = this.authStore.roleDisplayNames;

  public readonly activeGrantUserAndOwnGrants = this.authStore.activeGrantUserAndOwnGrants;

  public readonly activeOrgIdAndRole = this.authStore.activeOrgIdAndRole;

  /**
   * Sets the user in the authentication store and starts the inactivity tracking service.
   *
   * @param {User} user - The user object to set in the authentication store.
   * @returns {Promise<void>} A promise that resolves when the user is set and inactivity tracking is started.
   * @throws {Error} Throws an error if starting the inactivity tracking service fails.
   */
  public async setUser(user: User): Promise<void> {
    await this.asyncErrorHandler(
      async () => {
        this.authStore.setUser(user);
        this.inactivityService.startTracking();
      },
      {
        metadata: createErrorMetadata(
          'AuthFacade',
          'Failed to start tracking for inactivityService in AuthFacade.setUser',
          ErrorSeverityMap.High,
        ),
        rethrow: true,
      },
    );
  }

  /**
   * Clears the stored logout reason from the authentication store.
   *
   * @returns {void} This method does not return a value.
   */
  public clearLogoutReason(): void {
    this.authStore.clearLogoutReason();
  }

  /**
   * Navigates the user back to the login screen. Runs the standard logout, which
   * clears the local session state, ends the Hanko session and lands on the
   * login page — safe to call from the error pages, where there may be no
   * session left to clear.
   *
   * @param {LogoutReason} logoutReason - The reason for logging out, used when an active session exists.
   */
  public backToLogin(logoutReason: LogoutReason): void {
    void this.logout(logoutReason);
  }

  /**
   * Updates the authentication tokens by merging existing tokens with new ones
   * and persists specific tokens when provided.
   *
   * @param {Partial<AuthStateTokens>} tokens - Partial object containing one or more authentication tokens to update.
   * @returns {void} No return value.
   */
  public updateTokens(tokens: Partial<AuthStateTokens>): void {
    this.authStore.updateTokens(tokens);
  }

  /**
   * Records which identity provider established the current session so that the
   * guard and logout flow can branch provider-specific behavior.
   *
   * @param {AuthSource} source - The provider that established the session.
   */
  public setAuthSourceMarker(source: AuthSource): void {
    this.authStore.setAuthSourceMarker(source);
  }

  /**
   * Clears all stored authentication tokens by setting their values to null.
   * This includes the Kerberos access and refresh tokens and the Hanko session token.
   *
   * @returns {void} This method does not return a value.
   */
  public clearTokens(): void {
    this.authStore.clearTokens();
  }

  /**
   * Discards the whole in-memory session — user, grants, organizations, active
   * organization, provider marker and tokens — without the navigation and remote
   * sign-out that {@link logout} performs.
   *
   * For callers that have already invalidated the session at its source (e.g. the
   * bootstrap purge of a pre-Hanko Azure session) and only need the app to agree.
   * `clearTokens` alone is not enough there: the store hydrates user and grants from
   * storage independently of the tokens, so dropping only the tokens leaves a
   * half-session behind that still looks logged in (AML-DESK-AW).
   *
   * @returns {void} This method does not return a value.
   */
  public resetSessionState(): void {
    this.authStore.resetState();
  }

  /**
   * Logs the user out of the application, clears cached data, and resets the application state.
   * Protected against multiple simultaneous calls - only the first call will execute.
   *
   * @param {LogoutReason} logoutReason - The reason for logging out, used for analytics or system handling.
   * @returns {Promise<void>} A promise that resolves when the logout process is completed.
   */
  public async logout(logoutReason: LogoutReason): Promise<void> {
    if (this.isLoggingOut) {
      return;
    }
    this.isLoggingOut = true;

    try {
      if (logoutReason === LogoutReason.SessionExpired) {
        this.saveLastVisitedUrlToStorage();
      }

      // Captured before the storage is cleared: the sign-out and the impersonation return still need them.
      const { accessTokenKerberos, refreshTokenKerberos } = this.authStore.authTokens();
      this.authStore.setLogoutReason(logoutReason);
      this.processDocumentFacade.clearCache();
      clearExcept(this.storageService, [
        StorageServiceKeysMap.LogoutReason,
        StorageServiceKeysMap.PendingLastVisitedUrl,
        StorageServiceKeysMap.SecurityNudgeDismissedAt,
        StorageServiceKeysMap.RecentOrganizations,
      ]);
      this.inactivityService.stopTracking();
      this.globalLoadingService.stop();
      await this.authService.logout(logoutReason, { accessTokenKerberos, refreshTokenKerberos });
      this.authStore.resetState();
    } finally {
      this.isLoggingOut = false;
    }
  }

  /**
   * Extends the current session by a full idle window (30 minutes), dismisses any active
   * expiry warning, and notifies the user with a confirmation toast.
   *
   * @returns {void} This method does not return a value.
   */
  public extendSession(): void {
    this.inactivityService.extendSession();
    this.notificationService.success(this.translationService.getTranslation('sessionExpiry.extendedToast'));
  }

  /**
   * Loads grants and organizations using the provided access token, processes user permissions,
   * and sets up session grants accordingly.
   *
   * @param {string} accessTokenKerberos The access token used for authorization to retrieve grants and organizations.
   * @param {object} [options] Loading options.
   * @param {boolean} [options.skipOrgSideEffects] When true, refreshes grant/organization
   * data in the store but skips organization selection (session grant, contract request,
   * location-analysis flow). Used on in-place re-auth where the active org is already set,
   * so re-emitting it would make every org-scoped facade reload.
   * @returns {Promise<void>} A promise that resolves when the grants and organizations are successfully loaded and processed, or rejects if an error occurs.
   */
  public async loadGrants(accessTokenKerberos: string, options: { skipOrgSideEffects?: boolean } = {}): Promise<void> {
    if (!accessTokenKerberos) {
      return;
    }
    this.authStore.markGrantLoadingComplete(false);
    this.authStore.setGrantLoadingState(GrantLoadingStateMap.Loading);
    try {
      await this.asyncErrorHandler(
        async () => {
          const { grants, organizations } = await firstValueFrom(this.authService.getGrant(accessTokenKerberos));
          this.authStore.saveGrantsToStorage(grants, organizations);
          this.authStore.setOwnGrants(grants);
          this.authStore.setOrganizations(organizations);

          // Check if user has no grants
          if (!grants || grants.length === 0) {
            this.authStore.setGrantLoadingState(GrantLoadingStateMap.Empty);
            this.errorLoggingService.logError(new Error('User has no grants assigned'), {
              component: 'AuthFacade',
              action: 'loadGrants',
              grantsCount: grants?.length ?? 0,
            });
            this.authStore.markGrantLoadingComplete(true);
            return;
          }

          // In-place re-auth: the grant/organization data above is refreshed, but
          // the active organization is already established from the original login.
          // Skip organization selection so the session grant is not re-emitted —
          // that re-emit would make every org-scoped facade reload (KCP-1159).
          if (options.skipOrgSideEffects) {
            this.authStore.setGrantLoadingState(GrantLoadingStateMap.Success);
            return;
          }

          await this.waitForUserProfile();

          const hasWorkItemGrant = this.hasGrant(grants, [
            NamedRoleMap.WorkItemUser,
            NamedRoleMap.KycRiskEvaluationApiUser,
          ]);
          const hasLocationGrant = this.hasGrant(grants, [NamedRoleMap.LocationAnalysisUser]);
          const organizationId = this.authStore.determineOrganizationId(grants, organizations);

          if (hasWorkItemGrant && organizationId) {
            await this.setSessionGrant(organizationId, { suppressNotification: true });
          } else if (!hasLocationGrant && organizationId) {
            await this.setSessionGrant(organizationId, { suppressNotification: true });
            this.createContractRequest(organizationId, grants);
          }

          if (!hasWorkItemGrant && hasLocationGrant) {
            this.handleLocationAnalysisFlow(grants, organizations);
          }

          this.authStore.setGrantLoadingState(GrantLoadingStateMap.Success);
        },
        {
          metadata: createErrorMetadata('AuthFacade', 'Loading grants', ErrorSeverityMap.High),
          // The grant loading error state is the feedback
          silent: true,
          onError: (error) => {
            const message = error instanceof Error ? error.message : 'Unknown error';
            this.authStore.setErrorMessage(message);
            this.authStore.setOwnGrants(undefined);
            this.authStore.setOrganizations(undefined);
            this.authStore.setGrantLoadingState(GrantLoadingStateMap.Error);
            this.errorLoggingService.logError(error, {
              component: 'AuthFacade',
              action: 'loadGrants',
              errorMessage: message,
            });
            this.logger.debug('getGrantFailure', undefined, error);
          },
        },
      );
    } finally {
      this.authStore.markGrantLoadingComplete(true);
    }
  }

  /**
   * Retries loading grants using the current Kerberos access token and navigates to the homepage on success.
   * This is used by error pages to retry the grant loading process.
   *
   * @returns {Promise<void>} A promise that resolves once the grants are loaded and navigation is completed.
   */
  private retryLoadGrantsRunId = 0;

  public async retryLoadGrantsAndNavigateHome(): Promise<void> {
    const run = ++this.retryLoadGrantsRunId;
    const tokens = this.authTokens();
    if (!tokens.accessTokenKerberos) {
      return;
    }

    await this.loadGrants(tokens.accessTokenKerberos);
    if (run === this.retryLoadGrantsRunId) {
      void this.navigationService.navigateByUrl(AppPathsMap.Homepage);
    }
  }

  /**
   * Sets the session grant for the specified organization and performs necessary updates and notifications.
   *
   * @param {string} organizationId - The identifier of the organization for which the session grant is being set.
   * @param {SetSessionGrantOptions} [options] - Optional parameters for setting the session grant, such as whether to suppress notifications.
   * @returns {Promise<void>} A promise that resolves when the session grant is successfully set or rejects with an error if the operation fails.
   */
  private setSessionGrantRunId = 0;

  public async setSessionGrant(organizationId: string, options?: SetSessionGrantOptions): Promise<void> {
    if (!organizationId) {
      return;
    }
    const run = ++this.setSessionGrantRunId;
    this.recentOrganizationsService.recordVisit(organizationId as UUID);
    await this.asyncErrorHandler(
      async () => {
        const currentOrganizationId = this.organizationId();
        if (currentOrganizationId && currentOrganizationId !== organizationId) {
          this.processDocumentFacade.clearCache();
          this.riskAnalysisStore.reset();
          this.riskAnalysisService.clearCache();
        }
        this.workItemTypeService.clearWorkItemTypes();
        this.authStore.setOrganizationId(organizationId);
        await this.loadOrganizationIndustry(organizationId, () => run !== this.setSessionGrantRunId);
        if (run !== this.setSessionGrantRunId) {
          return;
        }
        if (!options?.suppressNotification) {
          this.notificationService.success(
            this.translationService.getTranslation('navigation.organizationWasSelectedSuccess'),
          );
        }
        if (this.shouldLoadWorkItemTypes()) {
          await this.workItemTypeService.loadWorkItemTypes();
        }
        // Load contract only for users that require it
        this.createContractRequest(organizationId);
      },
      {
        metadata: createErrorMetadata('AuthFacade', 'Error loading company data', ErrorSeverityMap.High),
        userMessage: this.translationService.getTranslation('navigation.organizationWasSelectedSuccessError'),
        rethrow: true,
      },
    );
  }

  /**
   * Select and return authentication tokens along with the user's own grant.
   *
   * @param {FeaturesAmlDeskWithGrants} [feature] - Optional feature that may influence grant resolution.
   * @param {NamedRole[]} [requiredRoles] - Optional array of roles required to resolve the user's grant.
   * @returns {Signal<AuthStateTokenAndOwnGrant>} A signal that emits an object containing authentication state tokens and the user's own grant.
   */
  public selectAuthTokensAndOwnGrant(
    feature?: FeaturesAmlDeskWithGrants,
    requiredRoles?: NamedRole[],
  ): Signal<AuthStateTokenAndOwnGrant> {
    return this.authStore.selectAuthTokensAndOwnGrant(feature, requiredRoles);
  }

  /**
   * Saves the current URL to storage for later sync to backend after re-login.
   * Used when session expires and we can't make API calls.
   */
  private saveLastVisitedUrlToStorage(): void {
    const currentUrl = this.navigationService.getCurrentUrl();
    if (!currentUrl) {
      return;
    }

    const excludedRoutes = [
      AppPathsMap.Homepage,
      `/${AppPathsMap.Logout}`,
      `/${AppPathsMap.Error.BackendUnavailable}`,
      `/${AppPathsMap.Error.LoginError}`,
      `/${AppPathsMap.Error.SessionUnavailable}`,
      `/${AppPathsMap.Error.UserNotActive}`,
    ];
    const isExcluded = excludedRoutes.some((route) => currentUrl === route || currentUrl.startsWith(route));

    if (isExcluded) {
      return;
    }

    // Persist only the path (drop the query string and fragment) so no query
    // parameters — which could carry search terms or other sensitive values —
    // are written to localStorage (D-02). The route alone is enough to restore
    // the user's location after re-login.
    const separatorIndex = currentUrl.search(/[?#]/);
    const pathOnly = separatorIndex === -1 ? currentUrl : currentUrl.slice(0, separatorIndex);
    this.storageService.set(StorageServiceKeysMap.PendingLastVisitedUrl, pathOnly);
  }

  /**
   * Waits until the user's profile is available.
   * This method listens for a signal indicating the availability of a user profile
   * and resolves once the signal is received or becomes truthy.
   *
   * @returns {Promise<void>} A promise that resolves once the user profile is available.
   */
  private async waitForUserProfile(): Promise<void> {
    if (this.user()) {
      return;
    }
    await firstValueFrom(signalToObservable(this.injector, this.user).pipe(filter(Boolean), take(1)));
  }

  /**
   * Handles the flow for analyzing location data based on user grants and organizations.
   * This method checks the availability of grants and organizations, determines the appropriate organization ID,
   * and sets the organization ID signal while also storing the value in the storage service.
   * Additionally, it initiates a contract creation request for the determined organization.
   *
   * @param {readonly ResponseUserGrantDto[]} grants - User grants
   * @param {readonly ResponseLeanOrganizationDto[]} organizations - Available organizations
   * @returns {void} This method does not return a value.
   */
  private handleLocationAnalysisFlow(
    grants: readonly ResponseUserGrantDto[],
    organizations: readonly ResponseLeanOrganizationDto[],
  ): void {
    if (!grants || !organizations || organizations.length === 0) {
      return;
    }
    const organizationId = this.authStore.determineOrganizationId(grants, organizations);
    if (!organizationId) {
      return;
    }
    this.authStore.setOrganizationId(organizationId);
    this.createContractRequest(organizationId, grants);
  }

  /**
   * Determines if any of the provided roles are included within the user's granted roles.
   *
   * @param {ResponseUserGrantDto[]} grants - An array of user grant objects that represent the user's granted roles and permissions.
   * @param {NamedRole[]} roles - An array of roles to check against the user's granted roles.
   * @returns {boolean} Returns true if at least one of the provided roles is included in the user's grants, otherwise false.
   */
  private hasGrant(grants: ResponseUserGrantDto[], roles: NamedRole[]): boolean {
    return grants.some((grant) => roles.includes(grant.roleDisplayName));
  }

  /**
   * Determines whether work item types should be loaded based on the current organization grants.
   * Checks if any grant for the current organization has WorkItemUser or KycRiskEvaluationApiUser role.
   *
   * @returns {boolean} True if work item types should be loaded, otherwise false.
   */
  private shouldLoadWorkItemTypes(): boolean {
    const ownGrants = this.ownGrants();
    const organizationId = this.organizationId();
    return (
      !!ownGrants &&
      !!organizationId &&
      ownGrants.some(
        (grant) =>
          grant.organizationId === organizationId &&
          (grant.roleDisplayName === NamedRoleMap.WorkItemUser ||
            grant.roleDisplayName === NamedRoleMap.KycRiskEvaluationApiUser),
      )
    );
  }

  /**
   * Creates a contract request by setting the query for the contract store based on the given organization ID.
   *
   * @param {string | undefined} organizationId - The identifier of the organization. If undefined, no query will be set.
   * @param {readonly ResponseUserGrantDto[]} [grants] - Optional user grants to check for risk analysis permission
   * @returns {void} This method does not return a value.
   */
  private createContractRequest(organizationId: string | undefined, grants?: readonly ResponseUserGrantDto[]): void {
    if (!organizationId || !this.hasRiskAnalysisGrant(organizationId, grants)) {
      return;
    }
    this.contractStore.setQuery({ organizationId });
  }

  /**
   * Determines whether the user has the Risk Analysis grant for a specific organization.
   *
   * @param {string} [organizationId] - Optional organization ID to filter grants. If not provided, uses the current organization.
   * @param {readonly ResponseUserGrantDto[]} [grants] - An optional array of user grants. If not provided, defaults to the user's own grants.
   * @returns {boolean} - Returns true if the user has the Risk Analysis grant for the specified organization, otherwise returns false.
   */
  private hasRiskAnalysisGrant(organizationId?: string, grants?: readonly ResponseUserGrantDto[]): boolean {
    const grantList = grants ?? this.ownGrants();
    if (!grantList) {
      return false;
    }
    const orgId = organizationId ?? this.organizationId();
    if (!orgId) {
      return false;
    }
    const orgGrants = grantList.filter((grant) => grant.organizationId === orgId);
    return this.hasGrant(orgGrants, [NamedRoleMap.RiskAnalysisCustomer]);
  }

  /**
   * Loads the organization industry by fetching organization details and then the industry name.
   * Only executes if the user has RiskAnalysis grant for the specific organization.
   * Fails silently if endpoints are unavailable to prevent app from breaking.
   *
   * @param {string} organizationId - The identifier of the organization.
   * @param {() => boolean} isStale - Predicate checked right before the store write; when it
   * returns true a newer organization selection has started and the fetched industry is dropped
   * instead of overwriting the active organization's value.
   * @returns {Promise<void>} A promise that resolves when the organization industry is loaded.
   */
  private async loadOrganizationIndustry(organizationId: string, isStale: () => boolean): Promise<void> {
    if (!this.hasRiskAnalysisGrant(organizationId)) {
      return;
    }

    try {
      const orgDetails = await this.organizationService.getOrganizationDetails(organizationId);
      const industry = await this.organizationService.getIndustryName(orgDetails.mainIndustryId);
      if (isStale()) {
        return;
      }
      this.authStore.setOrganizationIndustry(industry);
    } catch (error) {
      this.logger.debug('Failed to load organization industry', undefined, error);
    }
  }
}
